<div [formGroup]="form" class="step-form">

  <!-- ── Intro note ── -->
  <div class="intro-note">
    <mat-icon class="intro-icon">info_outline</mat-icon>
    <span>
      Quota Limit is optional. If you choose <strong>Do Nothing</strong>, no budget cap is enforced.
      Selecting any other action requires a budget threshold and a mandatory 100% notification band.
    </span>
  </div>

  <!-- ── Action at 100% ── -->
  <div class="section-label">
    <mat-icon class="section-icon">bolt</mat-icon>
    <span>Action when budget is fully consumed (100%)</span>
  </div>

  <div class="action-tile-row">
    <!-- Do Nothing tile -->
    <div
      class="action-tile"
      [class.action-tile--selected]="selectedAction === 'DoNothing'"
      (click)="selectAction('DoNothing')"
      (keyup.enter)="selectAction('DoNothing')"
      tabindex="0" role="button"
      [attr.aria-pressed]="selectedAction === 'DoNothing'"
      aria-label="Do Nothing">
      <mat-icon class="tile-icon">block</mat-icon>
      <div class="tile-content">
        <strong>Do Nothing</strong>
        <p class="tile-desc">No quota limit — resources run without a budget cap.</p>
      </div>
    </div>

    <div
      *ngFor="let tile of quotaActionTiles"
      class="action-tile"
      [class.action-tile--selected]="selectedAction === tile.value"
      (click)="selectAction(tile.value)"
      (keyup.enter)="selectAction(tile.value)"
      tabindex="0" role="button"
      [attr.aria-pressed]="selectedAction === tile.value"
      [attr.aria-label]="tile.label">
      <mat-icon class="tile-icon">{{ tile.icon }}</mat-icon>
      <div class="tile-content">
        <strong>{{ tile.label }}</strong>
        <p class="tile-desc">{{ tile.description }}</p>
      </div>
    </div>
  </div>

  <!-- ── Quota-enabled sections ── -->
  <ng-container *ngIf="isQuotaEnabled">

    <!-- ── Budget row: threshold + additional (ExtendQuota) on same row ── -->
    <div class="section-label">
      <mat-icon class="section-icon">currency_rupee</mat-icon>
      <span>Budget settings</span>
    </div>

    <div class="budget-row">
      <mat-form-field appearance="outline" class="amount-field">
        <mat-label>Budget threshold (₹ INR)</mat-label>
        <span matTextPrefix>₹&nbsp;</span>
        <input matInput type="number" formControlName="thresholdAmount" min="1" step="0.01" placeholder="e.g. 5000">
        <mat-error *ngIf="form.hasError('thresholdRequired')">
          Budget threshold is required and must be greater than 0.
        </mat-error>
      </mat-form-field>

      <mat-form-field *ngIf="selectedAction === 'ExtendQuota'" appearance="outline" class="amount-field">
        <mat-label>Additional budget on extension (₹ INR)</mat-label>
        <span matTextPrefix>₹&nbsp;</span>
        <input matInput type="number" formControlName="additionalAmount" min="1" step="0.01" placeholder="e.g. 1000">
        <mat-error *ngIf="form.hasError('additionalAmountRequired')">
          Additional amount is required and must be greater than 0.
        </mat-error>
      </mat-form-field>
    </div>

    <!-- ── Notification bands ── -->
    <div class="section-label">
      <mat-icon class="section-icon">notifications_active</mat-icon>
      <span>Notification bands</span>
    </div>

    <p class="bands-hint">
      Configure who gets notified at each spend threshold.
      The <strong>100% band</strong> is mandatory and fires when the budget is fully consumed.
      You can add optional bands at lower percentages (e.g. 75%, 90%).
    </p>

    <div class="bands-add-row">
      <button mat-stroked-button type="button" (click)="addCustomBand()">
        <mat-icon>add</mat-icon> Add Notification Band
      </button>
    </div>

    <mat-accordion formArrayName="notificationBands" class="bands-accordion">
      <mat-expansion-panel
        *ngFor="let _ of bandsArray.controls; let bi = index"
        [formGroupName]="bi"
        [expanded]="expandedBandIndex === bi"
        (opened)="onBandExpanded(bi)"
        (closed)="onBandClosed(bi)"
        class="band-panel"
        [class.band-panel--fixed]="isFixedBand(bi)">

        <!-- Panel header -->
        <mat-expansion-panel-header>
          <mat-panel-title class="band-panel-title">
            <mat-icon class="band-icon" [class.band-icon--fixed]="isFixedBand(bi)">
              {{ isFixedBand(bi) ? 'lock' : 'percent' }}
            </mat-icon>
            <span *ngIf="isFixedBand(bi)" class="band-fixed-label">100% — Budget fully consumed</span>
            <span *ngIf="!isFixedBand(bi)" class="band-custom-label">
              {{ getBandGroup(bi).get('triggerPercentage')?.value || '?' }}% threshold
            </span>
          </mat-panel-title>
          <mat-panel-description *ngIf="expandedBandIndex !== bi" class="band-panel-desc">
            {{ getBandTeamNames(bi) }}
          </mat-panel-description>
        </mat-expansion-panel-header>

        <!-- Panel content -->

        <!-- Fixed band note -->
        <div class="band-fixed-note" *ngIf="isFixedBand(bi)">
          <mat-icon class="note-icon">info_outline</mat-icon>
          <span>Participants are automatically notified by the system. Only configure additional team notifications here.</span>
        </div>

        <!-- Editable percentage + remove for custom bands -->
        <div *ngIf="!isFixedBand(bi)" class="band-controls-row">
          <mat-form-field appearance="outline" class="percentage-field">
            <mat-label>Trigger at (%)</mat-label>
            <input matInput type="number" formControlName="triggerPercentage"
                   min="1" max="99" step="1" placeholder="e.g. 75">
            <span matTextSuffix>%</span>
          </mat-form-field>
          <button mat-stroked-button type="button" color="warn" (click)="removeBand(bi)"
                  class="remove-band-btn" aria-label="Remove band">
            <mat-icon>delete</mat-icon> Remove Band
          </button>
        </div>

        <!-- Per-band notifications -->
        <div class="band-notifications">
          <div class="notif-toolbar">
            <button mat-stroked-button type="button" (click)="addNotificationToBand(bi)" class="add-team-btn">
              <mat-icon>person_add</mat-icon> Add Team
            </button>
          </div>

          <div formArrayName="notifications">
            <div
              *ngFor="let __ of getBandNotificationsArray(bi).controls; let ni = index"
              [formGroupName]="ni"
              class="notif-row">

              <!-- Team selector -->
              <mat-form-field appearance="outline" class="team-field">
                <mat-label>Team</mat-label>
                <mat-select formControlName="teamId" (selectionChange)="onTeamSelected($event.value, bi, ni)">
                  <mat-option *ngFor="let team of selectableTeams" [value]="team.id">{{ team.name }}</mat-option>
                </mat-select>
              </mat-form-field>

              <!-- User checkboxes -->
              <div class="user-select-section" *ngIf="getMembersForRow(bi, ni).length > 0">
                <p class="field-label">Notify users:</p>
                <div class="user-checkboxes">
                  <mat-checkbox
                    *ngFor="let member of getMembersForRow(bi, ni)"
                    [checked]="isUserSelected(member.id, bi, ni)"
                    (change)="toggleUserId(member.id, bi, ni)">
                    {{ member.name }}
                  </mat-checkbox>
                </div>
              </div>

              <p *ngIf="getNotificationRow(bi, ni).get('teamId')?.value && getMembersForRow(bi, ni).length === 0"
                 class="no-members-hint">
                No members available for this team.
              </p>

              <!-- Channels -->
              <div class="channel-row">
                <p class="field-label">Channels:</p>
                <mat-checkbox formControlName="emailChannel">Email</mat-checkbox>
                <mat-checkbox formControlName="appChannel">In-App</mat-checkbox>
              </div>

              <!-- Remove notification -->
              <button mat-icon-button type="button" (click)="removeNotificationFromBand(bi, ni)"
                      class="remove-notif-btn" aria-label="Remove notification row">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
          </div>

          <p *ngIf="getBandNotificationsArray(bi).length === 0" class="no-notif-hint">
            <ng-container *ngIf="isFixedBand(bi)">
              At least one team notification is recommended for the 100% band.
            </ng-container>
            <ng-container *ngIf="!isFixedBand(bi)">
              No teams configured. Add a team to receive notifications at this threshold.
            </ng-container>
          </p>
        </div>

      </mat-expansion-panel>
    </mat-accordion>

  </ng-container>

</div>
